import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ShoppingCart,
  Plus,
  Send,
  Trash2,
  Package,
  Calendar,
  FileText,
  Check,
  Clock,
  AlertCircle,
  Building2,
  ChevronDown,
  ChevronRight,
  Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { motion, AnimatePresence } from "framer-motion";

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  
  // Form state
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [orderItems, setOrderItems] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, suggestionsRes, productsRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/order-suggestions`),
        axios.get(`${API}/products`)
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setSuggestions(suggestionsRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const openNewOrder = () => {
    setSelectedSupplier("");
    setOrderNumber("");
    setNotes("");
    setOrderItems([]);
    setDialogOpen(true);
  };

  const addItemToOrder = (productId) => {
    if (orderItems.find(i => i.product_id === productId)) {
      toast.info("Produkt bereits in Bestellung");
      return;
    }
    
    const product = products.find(p => p.id === productId);
    if (product) {
      setOrderItems([...orderItems, {
        product_id: productId,
        product_name: product.name,
        quantity_ordered: 1
      }]);
    }
  };

  const updateItemQuantity = (index, quantity) => {
    const newItems = [...orderItems];
    newItems[index].quantity_ordered = parseInt(quantity) || 0;
    setOrderItems(newItems);
  };

  const removeItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const addSuggestionsFromSupplier = () => {
    if (!selectedSupplier) {
      toast.error("Bitte zuerst Lieferant auswählen");
      return;
    }
    
    const supplierSuggestions = suggestions.filter(s => s.best_supplier_id === selectedSupplier);
    const newItems = supplierSuggestions.map(s => ({
      product_id: s.product_id,
      product_name: s.product_name,
      quantity_ordered: Math.ceil(s.needed / (s.package_size || 1))
    }));
    
    // Merge with existing items
    const merged = [...orderItems];
    newItems.forEach(item => {
      if (!merged.find(i => i.product_id === item.product_id)) {
        merged.push(item);
      }
    });
    
    setOrderItems(merged);
    toast.success(`${supplierSuggestions.length} Vorschläge hinzugefügt`);
  };

  const handleCreateOrder = async () => {
    if (!selectedSupplier) {
      toast.error("Bitte Lieferant auswählen");
      return;
    }
    if (orderItems.length === 0) {
      toast.error("Bitte Produkte hinzufügen");
      return;
    }

    try {
      await axios.post(`${API}/orders`, {
        supplier_id: selectedSupplier,
        order_number: orderNumber || null,
        notes: notes || null,
        items: orderItems.filter(i => i.quantity_ordered > 0)
      });

      toast.success("Bestellung erstellt");
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Erstellen der Bestellung");
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success("Status aktualisiert");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const deleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      await axios.delete(`${API}/orders/${orderToDelete}`);
      toast.success("Bestellung gelöscht");
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: "Entwurf", class: "bg-slate-100 text-slate-700" },
      ordered: { label: "Bestellt", class: "bg-blue-100 text-blue-700" },
      partial: { label: "Teillieferung", class: "bg-amber-100 text-amber-700" },
      complete: { label: "Abgeschlossen", class: "bg-emerald-100 text-emerald-700" },
      cancelled: { label: "Storniert", class: "bg-red-100 text-red-700" }
    };
    const badge = badges[status] || badges.draft;
    return <Badge className={badge.class}>{badge.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Bestellungen
          </h1>
          <p className="text-slate-500 mt-1">
            {orders.length} Bestellungen • {suggestions.length} Bestellvorschläge
          </p>
        </div>
        <Button
          onClick={openNewOrder}
          className="bg-rose-600 hover:bg-rose-700"
          data-testid="new-order-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neue Bestellung
        </Button>
      </div>

      {/* Suggestions Alert */}
      {suggestions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-medium text-amber-900">
                  {suggestions.length} Produkte mit niedrigem Bestand
                </p>
                <p className="text-sm text-amber-700">
                  Erstellen Sie eine Bestellung, um die Vorschläge automatisch hinzuzufügen
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Bestellungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              Noch keine Bestellungen
            </p>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <div key={order.id} className="border rounded-lg overflow-hidden">
                  {/* Order Header */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleOrderExpand(order.id)}
                  >
                    <div className="flex items-center gap-4">
                      {expandedOrders[order.id] ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{order.supplier_name}</span>
                          {order.order_number && (
                            <span className="text-sm text-slate-500">
                              #{order.order_number}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(order.order_date || order.created_at)}
                          <span>•</span>
                          {order.items_count} Position{order.items_count !== 1 ? "en" : ""}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold">
                        {order.total_gross?.toFixed(2) || "0.00"}€
                      </span>
                      {getStatusBadge(order.status)}
                    </div>
                  </div>

                  {/* Order Details (Expanded) */}
                  <AnimatePresence>
                    {expandedOrders[order.id] && (
                      <OrderDetails
                        orderId={order.id}
                        status={order.status}
                        onStatusChange={updateOrderStatus}
                        onDelete={() => {
                          setOrderToDelete(order.id);
                          setDeleteDialogOpen(true);
                        }}
                      />
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Neue Bestellung
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Supplier Selection */}
            <div className="space-y-2">
              <Label>Lieferant *</Label>
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger>
                  <SelectValue placeholder="Lieferant wählen" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bestellnummer (optional)</Label>
                <Input
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="z.B. B-2024-001"
                />
              </div>
              <div className="space-y-2">
                <Label>Notizen</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Interne Notizen"
                />
              </div>
            </div>

            {/* Add from suggestions */}
            {selectedSupplier && suggestions.filter(s => s.best_supplier_id === selectedSupplier).length > 0 && (
              <Button
                variant="outline"
                onClick={addSuggestionsFromSupplier}
                className="w-full"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Bestellvorschläge hinzufügen ({suggestions.filter(s => s.best_supplier_id === selectedSupplier).length})
              </Button>
            )}

            {/* Product Selection */}
            <div className="space-y-2">
              <Label>Produkt hinzufügen</Label>
              <Select onValueChange={addItemToOrder}>
                <SelectTrigger>
                  <SelectValue placeholder="Produkt auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Order Items */}
            {orderItems.length > 0 && (
              <div className="space-y-2">
                <Label>Bestellpositionen</Label>
                <div className="space-y-2">
                  {orderItems.map((item, index) => (
                    <div
                      key={item.product_id}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{item.product_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity_ordered}
                          onChange={(e) => updateItemQuantity(index, e.target.value)}
                          className="w-20 h-9"
                        />
                        <span className="text-sm text-slate-500">VPE</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-slate-400 hover:text-red-600"
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleCreateOrder}
                className="bg-rose-600 hover:bg-rose-700"
                disabled={!selectedSupplier || orderItems.length === 0}
              >
                <Check className="w-4 h-4 mr-2" />
                Bestellung erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bestellung löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden.
              Nur Entwürfe können gelöscht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteOrder}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Separate component for order details
const OrderDetails = ({ orderId, status, onStatusChange, onDelete }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 border-t bg-slate-50">
        <div className="animate-pulse h-20"></div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="border-t bg-slate-50"
    >
      <div className="p-4 space-y-4">
        {/* Items */}
        <div className="space-y-2">
          {order.items?.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-2 bg-white rounded border"
            >
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-slate-400" />
                <span>{item.product_name}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span>{item.quantity_received || 0} / {item.quantity_ordered} VPE</span>
                <span className="font-mono">
                  {((item.unit_price_gross || 0) * item.quantity_ordered * (item.package_size || 1)).toFixed(2)}€
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="text-sm text-slate-600 bg-white p-3 rounded border">
            <strong>Notizen:</strong> {order.notes}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {status === "draft" && (
            <>
              <Button
                size="sm"
                onClick={() => onStatusChange(orderId, "ordered")}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-1" />
                Als bestellt markieren
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={onDelete}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Löschen
              </Button>
            </>
          )}
          
          {status === "ordered" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStatusChange(orderId, "cancelled")}
            >
              Stornieren
            </Button>
          )}

          {order.supplier_email && status === "draft" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const subject = encodeURIComponent(`Bestellung ${order.order_number || ""}`);
                const body = encodeURIComponent(
                  order.items?.map(i => `${i.quantity_ordered}x ${i.product_name}`).join("\n") || ""
                );
                window.open(`mailto:${order.supplier_email}?subject=${subject}&body=${body}`);
              }}
            >
              <Mail className="w-4 h-4 mr-1" />
              Per E-Mail bestellen
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default OrdersPage;
